import { coffreEntryIsFavorite } from "./coffreAppUtils.js";

export const COFFRE_LIST_SORT_STORAGE_KEY = "coffre_list_sort";

/** @typedef {'name' | 'date' | 'size'} CoffreListSortKey */
/** @typedef {'asc' | 'desc'} CoffreListSortDir */

/** @returns {{ key: CoffreListSortKey | null, dir: CoffreListSortDir }} */
export function readCoffreListSort() {
  try {
    const raw = localStorage.getItem(COFFRE_LIST_SORT_STORAGE_KEY);
    if (!raw) return { key: null, dir: "asc" };
    const parsed = JSON.parse(raw);
    const key =
      parsed?.key === "name" || parsed?.key === "date" || parsed?.key === "size" ? parsed.key : null;
    const dir = parsed?.dir === "desc" ? "desc" : "asc";
    return { key, dir };
  } catch {
    return { key: null, dir: "asc" };
  }
}

/** @param {CoffreListSortKey | null} key @param {CoffreListSortDir} dir */
export function writeCoffreListSort(key, dir) {
  try {
    localStorage.setItem(COFFRE_LIST_SORT_STORAGE_KEY, JSON.stringify({ key, dir }));
  } catch {
    /* ignore */
  }
}

function favoriteFirst(a, b) {
  const af = coffreEntryIsFavorite(a);
  const bf = coffreEntryIsFavorite(b);
  if (af === bf) return 0;
  return af ? -1 : 1;
}

function folderFirst(a, b) {
  const rank = (e) => {
    if (e?.kind === "shortcut") {
      return e?.shortcut_target?.kind === "folder" ? "folder" : "file";
    }
    return e?.kind;
  };
  const ar = rank(a);
  const br = rank(b);
  if (ar === br) return 0;
  return ar === "folder" ? -1 : 1;
}

function compareName(a, b) {
  return String(a.name ?? "").localeCompare(String(b.name ?? ""), "fr", {
    sensitivity: "base",
    numeric: true,
  });
}

function compareDate(a, b) {
  const ta = a.modified ? Date.parse(a.modified) : 0;
  const tb = b.modified ? Date.parse(b.modified) : 0;
  return ta - tb;
}

function compareSize(a, b) {
  const sa = a.kind === "file" && a.size != null ? Number(a.size) : -1;
  const sb = b.kind === "file" && b.size != null ? Number(b.size) : -1;
  return sa - sb;
}

/**
 * Tri client de la liste Coffre — favoris toujours en tête (comme le serveur),
 * puis dossiers avant fichiers, puis critère actif ou nom A→Z par défaut.
 * @param {Array<{ kind?: string, name?: string, modified?: string, size?: number | null, is_favorite?: boolean }>} entries
 * @param {CoffreListSortKey | null} sortKey
 * @param {CoffreListSortDir} sortDir
 */
export function sortCoffreEntries(entries, sortKey, sortDir) {
  if (!Array.isArray(entries) || entries.length < 2) return entries;

  const mul = sortDir === "desc" ? -1 : 1;
  return [...entries].sort((a, b) => {
    const favCmp = favoriteFirst(a, b);
    if (favCmp !== 0) return favCmp;

    const kindCmp = folderFirst(a, b);
    if (kindCmp !== 0) return kindCmp;

    if (!sortKey) {
      return compareName(a, b);
    }

    let cmp = 0;
    if (sortKey === "name") cmp = compareName(a, b);
    else if (sortKey === "date") cmp = compareDate(a, b);
    else if (sortKey === "size") cmp = compareSize(a, b);

    if (cmp !== 0) return cmp * mul;
    return compareName(a, b);
  });
}
